/**
 * Data migrations.
 *
 * The console's half of a rule the server enforces: **a dry run is required
 * before a real one, and the dry run must be the most recent thing that
 * happened to that migration.** A dry run from before the last live run
 * describes a database that no longer exists.
 *
 * The state here is derived rather than stored, so a run recorded by a script
 * or by a future scheduled backfill shows up on this page without anybody
 * having to remember to tell it.
 */

export type MigrationMode = "dry" | "live";

export interface Migration {
  id: string;
  description: string;
}

export interface MigrationRun {
  id: string;
  migrationId: string;
  mode: MigrationMode;
  at: string;
  actorUid: string;
  affected: number;
  note?: string;
  ok: boolean;
  error?: string;
}

export type MigrationState = "never" | "dryRunOnly" | "applied" | "failed";

export function runsFor(runs: readonly MigrationRun[], migrationId: string): MigrationRun[] {
  return runs
    .filter((run) => run.migrationId === migrationId)
    .sort((a, b) => b.at.localeCompare(a.at));
}

export function stateOf(runs: readonly MigrationRun[], migrationId: string): MigrationState {
  const mine = runsFor(runs, migrationId);

  if (mine.length === 0) return "never";
  if (mine[0] && !mine[0].ok) return "failed";
  if (mine.some((run) => run.mode === "live" && run.ok)) return "applied";
  return "dryRunOnly";
}

export const STATE_KEY: Record<MigrationState, string> = {
  never: "migration.state.never",
  dryRunOnly: "migration.state.dryRunOnly",
  applied: "migration.state.applied",
  failed: "migration.state.failed",
};

export const STATE_TONE: Record<MigrationState, string> = {
  never: "text-ink-muted",
  dryRunOnly: "text-cue-amber",
  applied: "text-cue-green",
  failed: "text-accent",
};

export type Readiness =
  | { ready: true; affected: number }
  | { ready: false; reasonKey: string };

/**
 * Whether the live button may be pressed.
 *
 * Mirrors the server's rule exactly. The button is disabled rather than hidden
 * and the reason is on the screen, because "why can I not press this" is a
 * question a console should answer where it is asked.
 */
export function readiness(runs: readonly MigrationRun[], migrationId: string): Readiness {
  const latest = runsFor(runs, migrationId)[0];

  if (!latest) return { ready: false, reasonKey: "migration.blocked.noDryRun" };
  if (latest.mode === "dry" && latest.ok) return { ready: true, affected: latest.affected };
  return { ready: false, reasonKey: "migration.blocked.dryRunStale" };
}

/** What a dry run said it would change, for the confirmation. */
export function lastDryRun(
  runs: readonly MigrationRun[],
  migrationId: string
): MigrationRun | undefined {
  return runsFor(runs, migrationId).find((run) => run.mode === "dry" && run.ok);
}
